import { createElement } from 'react';
import { Routes, Route, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tv, Database, Library, HardDrive, type LucideIcon } from 'lucide-react';
import { useSetContextualNav } from '@/components/layout/nav-context';
import { ChannelsDashboard } from './ChannelsDashboard';
import { StoragesDashboard } from './StoragesDashboard';
import { ContentLibraryDashboard } from './ContentLibraryDashboard';
import { CacheStatusPage } from './CacheStatusPage';
import { ChannelDetail } from './ChannelDetail';

/** Service-scope sidebar entries — one per top-level sub-page (slug = nested route). */
const FC_NAV: { slug: string; label: string; icon: LucideIcon }[] = [
  { slug: '', label: 'Channels', icon: Tv },
  { slug: 'storages', label: 'Storages', icon: Database },
  { slug: 'library', label: 'Content Library', icon: Library },
  { slug: 'cache', label: 'Cache & PVC', icon: HardDrive },
];

/**
 * FastChannel service console — owns the app's ContextualSidebar for one
 * service_uuid scope (Channels / Storages / Cache), same scope-swap model as the
 * AES / CDN / Compute / Kubernetes consoles. Sub-pages keep their own
 * `Container` + `PageHeader`; this wrapper only registers the sidebar and routes.
 */
export function FastChannelConsole() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { serviceUuid } = useParams<{ serviceUuid: string }>();
  const base = `/services/fastchannel/${serviceUuid}`;
  const hrefFor = (slug: string) => (slug ? `${base}/${slug}` : base);

  useSetContextualNav(() => {
    // First path segment after the service base → which top-level page is active.
    // `channels/:id` drill-downs and the index both highlight Channels (base).
    const sub = pathname.slice(base.length).split('/').filter(Boolean)[0] ?? '';
    const topLevel =
      sub === 'storages' || sub === 'library' || sub === 'cache' ? sub : '';
    return {
      sections: [
        {
          heading: 'FastChannel',
          items: FC_NAV.map((n) => ({
            id: hrefFor(n.slug),
            label: n.label,
            icon: createElement(n.icon, { size: 16 }),
            onSelect: () => navigate(hrefFor(n.slug)),
          })),
        },
      ],
      activeId: hrefFor(topLevel),
      back: { label: 'Back to Services', onSelect: () => navigate('/services') },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, pathname, navigate]);

  return (
    <Routes>
      <Route index element={<ChannelsDashboard />} />
      <Route path="storages" element={<StoragesDashboard />} />
      <Route path="library" element={<ContentLibraryDashboard />} />
      <Route path="cache" element={<CacheStatusPage />} />
      <Route path="channels/:channelUuid/*" element={<ChannelDetail />} />
    </Routes>
  );
}

export default FastChannelConsole;
